import { Feature, Polygon, MultiPolygon } from 'geojson';
import type { Map } from 'maplibre-gl';
import { getLevelProperty } from '@wemap/map';
import type { WemapMap } from '@wemap/map';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from './geoJsonSource.js';

type Building = Feature<Polygon | MultiPolygon> & {
    name: string;
    id: number;
    default_level: number;
    venue: number | null;
    levels: Array<{
        id: number;
        short_name: string;
        name: string;
        level: number;
        geometry: any;
    }>;
};

type BuildingsResponse = {
    count: number;
    previous: string | null;
    next: string | null;
    results: Building[];
};

const BUILDINGS_SOURCE = 'buildings-outline';
const LEVELS_SOURCE = 'levels-outline';

export default class BuildingsHandler {
    private readonly map: Map;
    emmid: number;
    buildings: Building[] = [];
    buildingFeatures: any[] = [];
    levelFeatures: any[] = [];
    
    layersID = [BUILDINGS_SOURCE, LEVELS_SOURCE];

    constructor(wemapMap: WemapMap, emmid: number) {
        this.map = wemapMap.maplibre;
        this.emmid = emmid;

        wemapMap.addSource(BUILDINGS_SOURCE, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        wemapMap.addLayer({
            id: BUILDINGS_SOURCE,
            type: 'line',
            source: BUILDINGS_SOURCE,
            paint: {
                'line-color': '#DAA520',
                'line-width': 2
            }
        }, { indoor: true });

        wemapMap.addSource(LEVELS_SOURCE, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        wemapMap.addLayer({
            id: LEVELS_SOURCE,
            type: 'line',
            source: LEVELS_SOURCE,
            paint: {
                'line-color': '#FFC107',
                'line-width': 1.5,
                'line-dasharray': [3, 3]
            }
        }, { indoor: true });
    }

    async fetchBuildings() {
        if (this.buildings.length > 0) {
            return this.buildings;
        }

        const totalResults: Building[] = [];
        const SLICE_SIZE = 20;

        let count = 0;
        let offset = 0;

        const url = new URL(`https://api.getwemap.com/v3.0/buildings/search`);
        url.searchParams.set('livemap', this.emmid.toString());
        url.searchParams.set('limit', SLICE_SIZE.toString());

        do {
            url.searchParams.set('offset', offset.toString());

            const response = await fetch(url.toString()).then(res => res.json()) as BuildingsResponse;
            count = count || response.count;

            totalResults.push(...response.results);
            offset += SLICE_SIZE;
        } while (totalResults.length < count);

        this.buildings = totalResults;
        return totalResults;
    }

    async init() {
        const buildings = await this.fetchBuildings();
        this.prepareFeatures(buildings);
    }

    prepareFeatures(buildings: Building[]) {
        this.buildingFeatures = buildings.map(building => ({
            type: 'Feature',
            geometry: building.geometry,
            properties: {
                id: building.id,
                name: building.name,
                default_level: building.default_level,
                venue: building.venue
            }
        }));

        this.levelFeatures = [];
        buildings.forEach(building => {
            if (building.levels && building.levels.length > 0) {
                building.levels.forEach(level => {
                    if (level.geometry) {
                        this.levelFeatures.push({
                            type: 'Feature',
                            geometry: level.geometry,
                            properties: {
                                building_id: building.id,
                                building_name: building.name,
                                level_id: level.id,
                                level_name: level.name,
                                level_short_name: level.short_name,
                                level: level.level,
                                ...getLevelProperty(level.level)
                            }
                        });
                    }
                });
            }
        });
    }

    showBuildings() {
        if (!this.buildingFeatures.length) {
            this.init().then(() => {
                this.showBuildings();
            });
            return;
        }

        if (!this.map.style) {
            return;
        }

        setGeoJsonSourceData(this.map, BUILDINGS_SOURCE, {
            type: 'FeatureCollection',
            features: this.buildingFeatures
        });

        setGeoJsonSourceData(this.map, LEVELS_SOURCE, {
            type: 'FeatureCollection',
            features: this.levelFeatures
        });
    }

    hideBuildings() {
        setGeoJsonSourceData(this.map, BUILDINGS_SOURCE, EMPTY_FEATURE_COLLECTION);
        setGeoJsonSourceData(this.map, LEVELS_SOURCE, EMPTY_FEATURE_COLLECTION);
    }
}
